// Shore-S — windowed image availability query

import { ee } from '../../services/earth-engine';

const S2_CLOUD_PROBABILITY = 'COPERNICUS/S2_CLOUD_PROBABILITY';

const isSentinel = (mission) => (mission.name || '').indexOf('COPERNICUS') !== -1;
const isLandsat = (mission) => (mission.name || '').indexOf('LANDSAT') !== -1;


const withSentinelBands = (windowed, geometry, startISO, endISO) => {
  const clean = windowed.map((img) => img.set('join_id', img.get('system:index')));
  const cloudProb = ee.ImageCollection(S2_CLOUD_PROBABILITY).filterBounds(geometry).filterDate(startISO, endISO);
  const joined = ee.ImageCollection(
    ee.Join.saveFirst('cloudProbability').apply({
      primary: clean,
      secondary: cloudProb,
      condition: ee.Filter.equals({ leftField: 'join_id', rightField: 'system:index' }),
    }),
  );
  return joined.map((img) => {
    const prob = ee.Image(img.get('cloudProbability')).select('probability');
    const cloudBin = prob.gt(50).rename('cloudBin').unmask(0);
    const validBin = img.select('B2').mask().rename('validBin');
    return img.addBands([cloudBin, validBin]);
  });
};

const withLandsatBands = (windowed) =>
  windowed.map((img) => {
    const qa = img.select('QA_PIXEL');
    const cloudMask = qa.bitwiseAnd(1 << 3).neq(0);
    const cloudConfidence = qa.bitwiseAnd(3 << 8).rightShift(8).gte(2);
    const cloudBin = cloudMask.or(cloudConfidence).rename('cloudBin').unmask(0);
    const validBin = qa.mask().rename('validBin');
    return img.addBands([cloudBin, validBin]);
  });

/**
 * @function windowMissionAvailability
 * @description Build a FeatureCollection with ONE feature per acquisition day in the window:
 * `{ dt, cloud }`, where `cloud` is the ROI cloud fraction (0..1) of that day's
 * mosaic. Works for Sentinel (S2 cloud probability) and Landsat (QA_PIXEL).
 *
 * @param {Object} mission - A single mission object (`.name`, `.shortname`).
 * @param {Object} geometry - An ee.Geometry (ROI).
 * @param {string} startISO - Inclusive window start.
 * @param {string} endISO - Exclusive window end.
 * **Called by:
 * @see {shoreS/saga}
 * @memberof module:ShoreS
 * @returns {Object} ee.FeatureCollection of `{ dt (millis), cloud (0..1) }`.
 */
export const windowMissionAvailability = (mission, geometry, startISO, endISO) => {
  const windowed = ee.ImageCollection(mission.name).filterBounds(geometry).filterDate(startISO, endISO);

  let scale;
  let tagged;
  if (isSentinel(mission)) {
    scale = 10;
    tagged = withSentinelBands(windowed, geometry, startISO, endISO);
  } else if (isLandsat(mission)) {
    scale = 30;
    tagged = withLandsatBands(windowed);
  } else {
    scale = 30;
    tagged = windowed.map((img) =>
      img.addBands(ee.Image(0).rename('cloudBin')).addBands(ee.Image(1).rename('validBin')),
    );
  }

  // Group scenes by calendar day and mosaic each day into a single image.
  const withDay = tagged.map((img) =>
    img.set('shoreS_day', ee.Date(img.get('system:time_start')).format('YYYY-MM-dd', 'UTC')),
  );
  const days = withDay.aggregate_array('shoreS_day').distinct();

  const sumOver = (image, band) =>
    image
      .multiply(ee.Image.pixelArea())
      .reduceRegion({ reducer: ee.Reducer.sum(), geometry, scale, maxPixels: 1e9, bestEffort: true })
      .get(band);

  const features = days.map((day) => {
    const dayCol = withDay.filter(ee.Filter.eq('shoreS_day', day));
    const cloudArea = sumOver(dayCol.select('cloudBin').mosaic(), 'cloudBin');
    const validArea = sumOver(dayCol.select('validBin').mosaic(), 'validBin');
    const fraction = ee.Number(cloudArea).divide(ee.Number(validArea));
    return ee.Feature(null, {
      dt: ee.Date(ee.String(day)).millis(),
      cloud: fraction,
    });
  });

  return ee.FeatureCollection(features);
};

/**
 * @function acquireDayMosaic
 * @description Builds the same per-day mosaic `windowMissionAvailability` uses internally,
 * for a single calendar day, for thumbnail generation and map display. `day` is a day-only
 * ISO string (e.g. "2017-08-02"), which is all Shore-S ever tracks per entry — unlike
 * `mission.algorithms.acquire`, which expects an exact timestamp and throws
 * `List.get: List is empty` when it tries to read the (empty) result.
 * @param {Object} mission - A single mission object (`.name`).
 * @param {string} day - Day-only ISO string, e.g. "2017-08-02".
 * @param {Object} geometry - An ee.Geometry (ROI).
 * **Called by:
 * @see {shoreS/saga}
 * @memberof module:ShoreS
 * @returns {Object} ee.Image — that day's mosaic, clipped to the ROI.
 */
export const acquireDayMosaic = (mission, day, geometry) => {
  const start = ee.Date(day);
  const end = start.advance(1, 'day');
  return ee.ImageCollection(mission.name)
    .filterBounds(geometry)
    .filterDate(start, end)
    .mosaic()
    .clip(geometry)
    .set('system:time_start', start.millis());
};
